// @dynamic-i18n-keys
// Dynamic t() at line 69: key from deviationTypeToTranslationKey(type).
//   common.commission
//   common.poll
//   common.status
//   common.journal
//   common.literature
//   common.deviation
import React, { useState, useMemo, useRef, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { PremiumTrash } from '@wix/da-ds/pkg/Icons/Confirmations';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import noop from '../../../../utils/noop';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { useDeviationAttributes } from '../../../hooks/useDeviationAttributes';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import UserAvatar from '../../../../User/Avatar';
import UserLink from '../../../../User/Link';
import UserWatchGhostButton from '../../../../WatchSuggestions/UserWatchGhostButton';
import Timestamp from '../../../../Timestamp';
import ConfirmationModal from '../../../../Popup/ConfirmationModal';
import { getDeviationEntityId } from '../../../../redux/normalizr/schemas/deviation';
import useDuperbrowseLink from '../../../../Duperbrowse/hooks/useDuperbrowseLink';
import { DeviationLink } from '../../../../Duperbrowse';
import ThumbActions from '../../../Helper/ThumbActions';
import {
  getDeviationType,
  getDeviationUrl,
  deviationTypeToTranslationKey,
  getDeviationResourceType,
} from '../../../_helpers';
import PriceAndSubscribeButton from '../PriceAndSubscribeButton';
import {
  BiData,
  DeleteDeviationConfirmClickBiEvent,
  WatchMenuItemSelectionBiEvent,
} from '@wix/da-bi/pkg/events';

import s from './FeedCardHeader.scss';

export interface Props {
  deviation: PapiDeviation;
  className?: string;
  extraActions?: NullableDropdownItem[];
  disableScrollRestoration?: boolean;
  withWatchOrSubscribeButton?: boolean;
  showReportModal?: () => void;
  deleteDeviation?: () => void;
}

const subscriptionReferralType = { referral_info: ReferralType.LOCKED_CONTENT };

const FeedCardHeader: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  className,
  extraActions = [],
  disableScrollRestoration,
  withWatchOrSubscribeButton = true,
  showReportModal = noop,
  deleteDeviation = noop,
}) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const isDesktop = !useContext(MobileContext);
  const [isShowingConfirmation, setShowingConfirmation] = useState(false);
  const { isOwner, tier, needsSubscription } = useSubscriptionTier(deviation);

  const type = getDeviationType(deviation);
  const typeName = t(deviationTypeToTranslationKey(type));

  // add standard post actions: report and go to page
  const { isReportable } = useDeviationAttributes(deviation, isOwner);

  // duperbrowse for the canonical link
  const duperRef = useRef<HTMLDivElement>(null);
  const onDuperbrowseClick = useDuperbrowseLink(
    getDeviationEntityId(deviation),
    duperRef,
    undefined,
    disableScrollRestoration
  );

  let watchOrSubscribeButton: React.ReactNode = null;
  if (isDesktop && withWatchOrSubscribeButton) {
    watchOrSubscribeButton =
      !!tier && needsSubscription ? (
        <BiLoggerContextProvider value={subscriptionReferralType}>
          <PriceAndSubscribeButton tier={tier} />
        </BiLoggerContextProvider>
      ) : !isOwner ? (
        <UserWatchGhostButton author={deviation.author!} />
      ) : null;
  }

  const typeAndItemIds = useMemo(
    () => ({
      typeid: getDeviationResourceType(deviation),
      itemid: deviation.deviationId,
    }),
    [deviation]
  );

  const thumbActions: NullableDropdownItem[] = useMemo(
    () => [
      ...extraActions,
      deviation.isPublished && {
        value: 'goToPage',
        label: t('deviationview.feed.cardHeader.goToPage', {
          pageType: typeName,
        }),
        link: getDeviationUrl(deviation),
        onClick: onDuperbrowseClick,
        biData: BiData<WatchMenuItemSelectionBiEvent>({
          evid: 424,
          menu_item: 'goToPage',
          ...typeAndItemIds,
        }),
      },
      isReportable && {
        value: 'report',
        label: t('deviationview.feed.cardHeader.report'),
        onClick: () => requestAnimationFrame(showReportModal),
        biData: BiData<WatchMenuItemSelectionBiEvent>({
          evid: 424,
          menu_item: 'report',
          ...typeAndItemIds,
        }),
      },
      isOwner && {
        value: 'delete',
        label: t('deviationview.feed.cardHeader.delete'),
        onClick: () => setShowingConfirmation(true),
        biData: BiData<WatchMenuItemSelectionBiEvent>({
          evid: 424,
          menu_item: 'delete',
          ...typeAndItemIds,
        }),
      },
    ],
    [
      deviation,
      typeAndItemIds,
      extraActions,
      isReportable,
      showReportModal,
      isOwner,
      t,
      typeName,
      onDuperbrowseClick,
    ]
  );

  return (
    <div className={classnames(s['root'], className)}>
      {deviation.author && (
        <UserAvatar
          className={s['avatar']}
          user={deviation.author}
          withTooltip
          size={32}
          relatedItemBiData={typeAndItemIds}
        />
      )}

      <div className={s['left-block']}>
        {deviation.author && (
          <UserLink
            className={s['username']}
            user={deviation.author}
            withTooltip
            relatedItemBiData={{
              typeid: deviation.typeId,
              itemid: deviation.deviationId,
            }}
          />
        )}
        <DeviationLink className={s['timestamp']} deviation={deviation}>
          <Timestamp timestamp={deviation.publishedTime} showFullLengthOnMobile />
        </DeviationLink>
      </div>

      <div className={s['right-block']} ref={duperRef}>
        {watchOrSubscribeButton}

        {thumbActions.length > 0 && (
          <ThumbActions className={s['thumb-actions']} deviation={deviation} actions={thumbActions}>
            <IconButton icon={EllipsisIcon} aria-label={t('common.more')} size={IconSize.SMALL} />
          </ThumbActions>
        )}
      </div>

      {isOwner && (
        <ConfirmationModal
          isOpen={isShowingConfirmation}
          title={t('deviationview.feed.cardHeader.deleteConfirmation.confirm', {
            type: typeName,
          })}
          text={t('deviationview.feed.cardHeader.deleteConfirmation.cantGoBack')}
          confirmBtnLabel={t('deviationview.feed.cardHeader.deleteConfirmation.delete')}
          icon={<PremiumTrash />}
          confirmBtnType="danger"
          onConfirm={() => {
            logBiEvent(
              BiData<DeleteDeviationConfirmClickBiEvent>({
                evid: 376,
                ...typeAndItemIds,
              })
            );
            setShowingConfirmation(false);
            deleteDeviation();
          }}
          onCancel={() => setShowingConfirmation(false)}
        />
      )}
    </div>
  );
};
FeedCardHeader.displayName = 'FeedCardHeader';

export default FeedCardHeader;
